import { useEffect, useRef, useState, type ReactNode } from "react";
import { Car, ChevronDown, ChevronUp } from "lucide-react";

export function FilterSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between py-4 text-left"
      >
        <span className="text-sm font-semibold" style={{ color: "var(--foreground)" }}>
          {title}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4" style={{ color: "var(--muted-foreground)" }} />
        ) : (
          <ChevronDown className="h-4 w-4" style={{ color: "var(--muted-foreground)" }} />
        )}
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
}

export function RangeFilter({
  fromValue,
  toValue,
  fromPlaceholder,
  toPlaceholder,
  onChange,
}: {
  fromValue: string;
  toValue: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  onChange: (from: string, to: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={fromValue}
        placeholder={fromPlaceholder}
        onChange={(e) => onChange(e.target.value, toValue)}
        className="w-full rounded-md px-2 py-1.5 text-sm"
        style={{ border: "1px solid var(--border)", color: "var(--foreground)" }}
      />
      <span className="flex-shrink-0 text-sm" style={{ color: "var(--muted-foreground)" }}>
        –
      </span>
      <input
        type="number"
        value={toValue}
        placeholder={toPlaceholder}
        onChange={(e) => onChange(fromValue, e.target.value)}
        className="w-full rounded-md px-2 py-1.5 text-sm"
        style={{ border: "1px solid var(--border)", color: "var(--foreground)" }}
      />
    </div>
  );
}

export function CheckboxGroup({
  options,
  selected,
  onChange,
}: {
  options: { value: string; label: string; count: number }[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? options : options.slice(0, 7);

  function toggle(value: string) {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {visible.map(({ value, label, count }) => (
        <label key={value} className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={selected.includes(value)}
            onChange={() => toggle(value)}
            className="h-4 w-4 rounded"
            style={{ accentColor: "var(--accent)" }}
          />
          <span className="text-sm" style={{ color: "var(--foreground)" }}>
            {label}
          </span>
          <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>
            ({count})
          </span>
        </label>
      ))}
      {options.length > 7 && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="mt-1 text-left text-sm font-medium"
          style={{ color: "var(--accent)" }}
        >
          {showAll ? "Vis færre" : "Vis alle"}
        </button>
      )}
    </div>
  );
}

/**
 * Dual-handle range slider sitting above the text inputs.
 * min/max define the slider bounds; fromValue/toValue are the current values.
 */
export function RangeFilterWithSlider({
  fromValue,
  toValue,
  fromPlaceholder,
  toPlaceholder,
  min,
  max,
  onChange,
}: {
  fromValue: string;
  toValue: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  min: number;
  max: number;
  onChange: (from: string, to: string) => void;
}) {
  const low = fromValue !== "" ? Math.min(Number(fromValue), max) : min;
  const high = toValue !== "" ? Math.max(Number(toValue), min) : max;
  const trackRef = useRef<HTMLDivElement>(null);

  const lowPct = ((low - min) / (max - min)) * 100;
  const highPct = ((high - min) / (max - min)) * 100;

  function handleLowChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = Math.min(Number(e.target.value), high - 1);
    onChange(String(v === min ? "" : v), toValue);
  }

  function handleHighChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = Math.max(Number(e.target.value), low + 1);
    onChange(fromValue, String(v === max ? "" : v));
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Dual-range slider */}
      <div className="relative h-5 w-full" ref={trackRef}>
        {/* Track */}
        <div
          className="absolute top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full"
          style={{ background: "var(--border)" }}
        />
        {/* Active range */}
        <div
          className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full"
          style={{
            left: `${lowPct}%`,
            width: `${highPct - lowPct}%`,
            background: "var(--accent)",
          }}
        />
        {/* Low thumb */}
        <input
          type="range"
          min={min}
          max={max}
          value={low}
          onChange={handleLowChange}
          className="pointer-events-none absolute inset-0 h-full w-full appearance-none bg-transparent"
          style={{ zIndex: low > max - (max - min) * 0.1 ? 5 : 3 }}
        />
        {/* High thumb */}
        <input
          type="range"
          min={min}
          max={max}
          value={high}
          onChange={handleHighChange}
          className="pointer-events-none absolute inset-0 h-full w-full appearance-none bg-transparent"
          style={{ zIndex: 4 }}
        />
      </div>

      {/* Text inputs */}
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={fromValue}
          placeholder={fromPlaceholder}
          onChange={(e) => onChange(e.target.value, toValue)}
          className="w-full rounded-md px-2 py-1.5 text-sm"
          style={{ border: "1px solid var(--border)", color: "var(--foreground)" }}
        />
        <span className="flex-shrink-0 text-sm" style={{ color: "var(--muted-foreground)" }}>
          –
        </span>
        <input
          type="number"
          value={toValue}
          placeholder={toPlaceholder}
          onChange={(e) => onChange(fromValue, e.target.value)}
          className="w-full rounded-md px-2 py-1.5 text-sm"
          style={{ border: "1px solid var(--border)", color: "var(--foreground)" }}
        />
      </div>
    </div>
  );
}

export function EmptyState({ message, sub }: { message: string; sub?: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 rounded-3xl py-16 px-6 text-center animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{
        background: "rgba(255,255,255,0.5)",
        border: "1px dashed var(--border)",
        backdropFilter: "blur(8px)"
      }}
    >
      <div className="relative">
        <div className="absolute inset-0 bg-accent/10 blur-2xl rounded-full" />
        <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-xl border border-border">
          <Car className="h-10 w-10 text-muted-foreground/40" />
        </div>
      </div>
      <div className="max-w-[280px]">
        <p className="text-lg font-black tracking-tight text-[#1a2e35] bg-clip-text" style={{ fontFamily: "var(--font-display)" }}>
          {message}
        </p>
        {sub && (
          <p className="mt-1 text-sm font-medium text-muted-foreground leading-relaxed">
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}
